'use client';

import { ChevronLeft, ChevronRight } from '@lulalife/design-system/icons/line';
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { SETTINGS_PAGE_PADDING_X } from './SettingsPage';

export type SettingsTabStripItem = {
  /** Stable identity; also what `onSelect` reports. */
  key: string;
  label: string;
  /**
   * Draws a marker dot beside the label. For a tab holding a validation error the
   * reader is not currently looking at — the tablist is the one place that error
   * is discoverable from another tab. Optional and additive: every existing caller
   * omits it and renders exactly as before.
   */
  hasError?: boolean;
};

type SettingsTabStripProps = {
  tabs: SettingsTabStripItem[];
  activeKey: string | null;
  onSelect: (key: string) => void;
  /** Accessible name for the tablist. */
  ariaLabel: string;
  /** DOM id for a tab button — must match the panel's `aria-labelledby`. */
  getTabId: (key: string) => string;
  /** DOM id for the controlled tabpanel — must match the panel's `id`. */
  getPanelId: (key: string) => string;
  /**
   * Supplies the design's page padding, for a strip rendered OUTSIDE the page
   * content column and therefore inheriting none of it.
   *
   * Leave it off inside a `SettingsPage`, which already puts its children on the
   * content line — passing it there would push the tabs 24px past the rows they
   * label. Vendor Preferences (`MarketTabs`) renders its strip inside
   * `SettingsPage` and leaves this off; no current caller passes `true`.
   */
  standalone?: boolean;
  /**
   * Draws the labels the way DS `Tabs` does on Settings > Users: regular weight, and the
   * active label in the link colour instead of primary text. Opt-in, so General, Automations
   * and User detail keep the strong, primary-text labels they were built to. Only the label
   * style changes; the tablist, its panel contract and the overflow chevrons stay.
   */
  dsLabels?: boolean;
  className?: string;
};

// Chevron pagination step, as a fraction of the visible strip: page by most of a
// viewport, keeping some overlap for orientation.
const SCROLL_PAGE_RATIO = 0.8;

/**
 * The prototype's underline tab strip (`.vp-mkt`), scoped to settings.
 *
 * DS-GAP(Tabs rendering a tablist only): delete this when DS Tabs can leave the panels to its caller.
 *
 * Not DS `Tabs` as of 0.3.1: that component renders its own `role="tabpanel"`
 * per item, so a page that owns its panel elsewhere would end up with two panels
 * per tab and `aria-controls` pointing at the empty one. This strip is the
 * tablist only, and the caller keeps the tablist ↔ panel contract it already had.
 *
 * Keyboard behaviour is the WAI-ARIA tabs pattern: roving tabindex, Arrow/Home/End.
 * Overflow follows the DS Tabs pattern: no native scrollbar, edge chevrons that
 * appear only on a side with hidden tabs and stay out of the tab order, because
 * keyboard users already reach off-screen tabs through Arrow navigation.
 */
export const SettingsTabStrip = ({
  tabs,
  activeKey,
  onSelect,
  ariaLabel,
  getTabId,
  getPanelId,
  standalone = false,
  dsLabels = false,
  className,
}: SettingsTabStripProps) => {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateOverflow = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    setCanScrollLeft(strip.scrollLeft > 0);
    // 1px tolerance: scrollLeft can land on fractional pixels at the right edge.
    setCanScrollRight(strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateOverflow();
    const strip = stripRef.current;
    if (!strip) return;
    strip.addEventListener('scroll', updateOverflow, { passive: true });
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateOverflow) : null;
    observer?.observe(strip);
    return () => {
      strip.removeEventListener('scroll', updateOverflow);
      observer?.disconnect();
    };
  }, [updateOverflow]);

  // The tab set itself changes the strip's scrollWidth without firing scroll/resize.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `tabs` is the re-measure trigger, not read in the effect body
  useEffect(() => {
    updateOverflow();
  }, [tabs, updateOverflow]);

  const scrollByPage = (direction: -1 | 1) => {
    const strip = stripRef.current;
    if (!strip) return;
    strip.scrollBy({
      left: direction * strip.clientWidth * SCROLL_PAGE_RATIO,
      behavior: 'smooth',
    });
  };

  const focusTabAt = (index: number) => {
    const wrapped = (index + tabs.length) % tabs.length;
    const target = tabs[wrapped];
    if (!target) return;
    onSelect(target.key);
    tabRefs.current[target.key]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusTabAt(currentIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusTabAt(currentIndex - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusTabAt(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusTabAt(tabs.length - 1);
    }
  };

  // The gradient is written as an arbitrary property: it needs a token colour at a
  // specific stop, which no utility expresses, and it lets the strip visibly slide
  // under the chevron instead of hard-clipping.
  const labelClass = dsLabels ? 'typography-body-sm' : 'typography-label-md-strong';
  const padY = dsLabels ? 'py-[7px]' : 'py-space-6';
  const activeLabelClass = dsLabels ? 'text-text-link' : 'text-text-primary';
  const inactiveLabelClass = dsLabels ? 'text-text-tertiary' : 'text-text-secondary';
  const chevronBase =
    'absolute inset-y-0 z-10 inline-flex w-space-32 items-center justify-center border-0 p-0 text-icon-secondary cursor-pointer';

  return (
    <div
      data-lula-ds=""
      className={`relative flex min-w-0 border-b border-solid border-border-subtle font-sans${
        standalone ? ` ${SETTINGS_PAGE_PADDING_X}` : ''
      }${className ? ` ${className}` : ''}`}
    >
      {canScrollLeft ? (
        <button
          type="button"
          className={`${chevronBase} left-0 [background:linear-gradient(to_right,var(--color-bg-surface)_65%,transparent)]`}
          aria-label="Scroll tabs left"
          tabIndex={-1}
          onClick={() => scrollByPage(-1)}
        >
          <ChevronLeft aria-hidden="true" />
        </button>
      ) : null}

      <div
        // NO HORIZONTAL PADDING HERE. The first tab's left edge — and therefore
        // the active tab's underline — has to land on the same line as the page
        // title and every row label, which is the alignment the prototype builds
        // the whole page on (`.tabs` has no padding; `.tab` carries 16px of its
        // own, so the TEXT is inset while the underline is not).
        //
        // The overflow chevrons do not need a reserved gutter: they are
        // `position: absolute` against this strip's `relative` root, so they
        // never displace a tab. Each appears only on a side that has hidden tabs,
        // and its gradient lets the strip slide under it — by which point the
        // tab it overlaps is one being scrolled away, not the first tab at rest.
        //
        // `[flex-wrap:nowrap]` as an arbitrary property rather than the utility:
        // the utility's name is one this app already renders, so it would need a
        // compatibility guard, and the strip must never wrap — off-screen tabs are
        // reached by scrolling, which wrapping would silently defeat.
        className="flex min-w-0 flex-1 gap-space-6 overflow-x-auto [flex-wrap:nowrap] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label={ariaLabel}
        ref={stripRef}
      >
        {tabs.map((tab, index) => {
          const isActive = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              ref={(node) => {
                tabRefs.current[tab.key] = node;
              }}
              type="button"
              role="tab"
              id={getTabId(tab.key)}
              aria-selected={isActive}
              aria-controls={getPanelId(tab.key)}
              tabIndex={isActive ? 0 : -1}
              // The three borderless sides are zeroed one side at a time, on
              // purpose. Bootstrap's utilities API emits the all-sides zeroing
              // utility unlayered and important, which outranks every layered
              // declaration — so using it here also flattened the 2px underline
              // below, plus its style and colour. Measured on
              // /settings/automations beforehand: the active tab computed
              // `border-bottom: 0px none`, i.e. no active-tab indicator at all.
              // Bootstrap emits none of the per-side names, so this spelling
              // removes the collision instead of fighting it with an important
              // modifier. Section 4 of design-system-compat.css is the write-up;
              // tests/unit/Components/DS/SettingsTabStrip.test.tsx fails loudly
              // if the shorthand comes back.
              //
              // The ring is `focus-visible`, never `focus`, so a mouse click
              // leaves nothing behind. It is spelled out rather than left to the
              // browser because the user-agent default is an off-brand blue
              // rectangle; these are the three utilities every DS component uses
              // for its own focus treatment.
              className={`${labelClass} -mb-space-1 shrink-0 cursor-pointer whitespace-nowrap border-x-0 border-t-0 border-b-2 border-solid bg-transparent px-space-16 ${padY} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus ${
                isActive
                  ? `border-border-brand ${activeLabelClass}`
                  : `border-transparent ${inactiveLabelClass} hover:text-text-primary`
              }`}
              onClick={() => onSelect(tab.key)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              {tab.label}
              {tab.hasError ? (
                // A SIBLING SPAN, never part of the button's own className. The
                // per-side border zeroing above is load-bearing, and a marker
                // drawn by adding to that string is one edit away from flattening
                // the active-tab underline again.
                <span
                  aria-hidden="true"
                  className="ml-space-6 inline-block size-space-6 rounded-full bg-alert-error-icon align-middle"
                />
              ) : null}
              {/* The dot is decoration; this is what a screen reader hears. Kept
                  out of the dot's own element so the tab's accessible name reads
                  "{label} (has an error)" rather than the label alone. */}
              {tab.hasError ? <span className="sr-only"> (has an error)</span> : null}
            </button>
          );
        })}
      </div>

      {canScrollRight ? (
        <button
          type="button"
          className={`${chevronBase} right-0 [background:linear-gradient(to_left,var(--color-bg-surface)_65%,transparent)]`}
          aria-label="Scroll tabs right"
          tabIndex={-1}
          onClick={() => scrollByPage(1)}
        >
          <ChevronRight aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

export default SettingsTabStrip;
